import { Link } from "react-router-dom";
import { Bed, Bath, Users } from "lucide-react";
import EditableImage from "@/components/EditableImage";
import { getAuthHeaders } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyCard({ listing, onUpdated }) {
  const replaceFeatured = async (url) => {
    await axios.put(`${API}/admin/listings/${listing.id}/replace-image`, {
      image_url: url, field: "featured_image"
    }, { headers: getAuthHeaders(), withCredentials: true });
    if (onUpdated) onUpdated();
  };

  return (
    <div
      data-testid={`property-card-${listing.slug}`}
      className="group relative overflow-hidden bg-white border border-[#E2E8F0] hover:border-[#32A334]/30 hover:shadow-lg transition-all duration-500 rounded-lg"
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <EditableImage src={listing.featured_image} alt={listing.title} onReplace={replaceFeatured}>
          <img
            src={listing.featured_image}
            alt={listing.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </EditableImage>
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        {listing.is_featured && (
          <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-[#1C671D] text-white px-3 py-1 font-semibold rounded">
            Featured
          </span>
        )}
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-['DM_Serif_Display'] text-2xl font-semibold text-[#1A1A2E] mb-2">
          {listing.title}
        </h3>
        {listing.location && (
          <p className="text-[#64748B] text-xs uppercase tracking-[0.15em] mb-4">{listing.location}</p>
        )}
        <p className="text-[#64748B] text-sm leading-relaxed mb-6 line-clamp-2">
          {listing.short_description}
        </p>

        {/* Stats */}
        <div className="flex items-center gap-6 mb-6 text-sm text-[#64748B]">
          <span className="flex items-center gap-1.5"><Bed className="w-4 h-4 text-[#32A334]" /> {listing.bedrooms} Bed</span>
          <span className="flex items-center gap-1.5"><Bath className="w-4 h-4 text-[#32A334]" /> {listing.bathrooms} Bath</span>
          <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#32A334]" /> {listing.sleeps}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 flex-wrap">
          <Link
            to={`/properties/${listing.slug}`}
            data-testid={`view-details-${listing.slug}`}
            className="bg-[#1C671D] text-white hover:bg-[#145215] transition-colors duration-300 px-6 py-2.5 text-xs uppercase tracking-[0.15em] rounded"
          >
            View Details
          </Link>
          {listing.airbnb_url && (
            <a
              href={listing.airbnb_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`airbnb-link-${listing.slug}`}
              className="text-[#64748B] hover:text-[#FF5A5F] transition-colors duration-200 text-xs uppercase tracking-[0.15em] px-3 py-2.5"
            >
              Airbnb
            </a>
          )}
          {listing.vrbo_url && (
            <a
              href={listing.vrbo_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`vrbo-link-${listing.slug}`}
              className="text-[#64748B] hover:text-[#3B5998] transition-colors duration-200 text-xs uppercase tracking-[0.15em] px-3 py-2.5"
            >
              VRBO
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
